/**
 * AppleBillingProvider.ts
 *
 * Production Apple App Store billing via the AppStoreBillingPlugin (StoreKit 2).
 * Implements IBillingProvider so BillingManager treats iOS and Android uniformly.
 *
 * Purchase flow:
 *  1. initialize()         – connect to StoreKit
 *  2. getProducts()        – query App Store products
 *  3. launchPurchaseFlow() – user taps "Subscribe" → StoreKit sheet
 *  4. Plugin fires purchaseUpdate event with a signed JWS token
 *  5. VerifyPaywall sends JWS to `validate_purchase`/`apple-verify-purchase`
 *  6. Server grants entitlement, returns verified status
 *  7. finishTransaction()  – mark StoreKit transaction as done (only after step 6)
 */

import type { IBillingProvider, PurchaseOptions } from './IBillingProvider';
import type {
  BillingPlatform,
  BillingProduct,
  PurchaseResult,
  Subscription,
  RestoredPurchase,
  BillingEventListener,
  BillingEvent,
} from '../types';
import AppStoreBilling from '@/plugins/AppStoreBilling';
import { APPLE_VERIFIED_PRODUCT_ID } from '@/config/subscriptions';
import { delayMs, logIap, sanitizeIapError } from '@/billing/utils/iapLog';

const MAX_PRODUCT_FETCH_ATTEMPTS = 3;
// StoreKit 2 can return an empty product list during cold App Store/TestFlight catalog
// warm-up. Keep these delays intentionally long; shortening them can recreate Apple Review
// subscription-unavailable failures.
const RETRY_DELAY_BEFORE_ATTEMPT: Record<number, number> = { 2: 3000, 3: 7000 };

export class ProductFetchEmptyError extends Error {
  readonly code = 'PRODUCT_FETCH_EMPTY';

  constructor(message = 'No subscription products returned from App Store') {
    super(message);
    this.name = 'ProductFetchEmptyError';
  }
}

export class AppleBillingProvider implements IBillingProvider {
  readonly platform: BillingPlatform = 'apple';

  private ready = false;
  private listeners: Set<BillingEventListener> = new Set();
  private nativeListenerHandle: { remove: () => void } | null = null;

  // ─────────────────────────────────────────
  // IBillingProvider implementation
  // ─────────────────────────────────────────

  async initialize(): Promise<void> {
    if (this.ready) return;

    logIap('iap:init:start');
    try {
      await AppStoreBilling.initConnection();

      this.nativeListenerHandle = await AppStoreBilling.addListener(
        'purchaseUpdate',
        (event) => {
          const timestamp = new Date();
          if (event.type === 'purchase_updated' && event.jwsRepresentation) {
            const billingEvent: BillingEvent = {
              type:      'purchase_completed',
              platform:  'apple',
              timestamp,
              data: {
                transactionId:     event.transactionId,
                productId:         event.productId,
                jwsRepresentation: event.jwsRepresentation,
              },
            };
            this.notifyListeners(billingEvent);
          } else if (event.type === 'purchase_error') {
            const billingEvent: BillingEvent = {
              type:      'purchase_failed',
              platform:  'apple',
              timestamp,
              data: { error: event.error ?? 'Unknown purchase error' },
            };
            this.notifyListeners(billingEvent);
          }
        }
      );

      this.ready = true;
      logIap('iap:init:success');
    } catch (err) {
      const { code, message } = sanitizeIapError(err);
      logIap('iap:init:fail', { code, message });
      throw err;
    }
  }

  isReady(): boolean {
    return this.ready;
  }

  async getProducts(): Promise<BillingProduct[]> {
    this.assertReady();
    return this.fetchSubscriptionsWithRetry();
  }

  async launchPurchaseFlow(
    productId: string,
    options?: PurchaseOptions & { appAccountToken?: string }
  ): Promise<PurchaseResult> {
    if (!this.ready) {
      logIap('iap:purchase:blocked', { reason: 'not_initialized' });
      return {
        success: false,
        error: {
          code:        'NOT_INITIALIZED',
          message:     'Billing not initialized',
          recoverable: true,
        },
      };
    }

    if (!productId) {
      logIap('iap:purchase:blocked', { reason: 'missing_product_id' });
      return {
        success: false,
        error: {
          code:        'MISSING_PRODUCT_ID',
          message:     'Product ID is required',
          recoverable: false,
        },
      };
    }

    if (productId !== APPLE_VERIFIED_PRODUCT_ID) {
      logIap('iap:purchase:blocked', { reason: 'invalid_product_id' });
      return {
        success: false,
        error: {
          code:        'INVALID_PRODUCT_ID',
          message:     'Product ID does not match App Store subscription',
          recoverable: false,
        },
      };
    }

    logIap('iap:purchase:start', { sku: productId });

    try {
      const result = await AppStoreBilling.requestSubscription({
        productId,
        appAccountToken: options?.appAccountToken,
      });

      if (result.success && result.jwsRepresentation) {
        return {
          success:          true,
          transactionId:    result.transactionId,
          productId:        result.productId,
          purchaseToken:    result.jwsRepresentation,
        };
      }

      const { code, message } = sanitizeIapError({
        code:    result.errorCode,
        message: result.errorMessage,
      });
      logIap('iap:purchase:error', { code, message });

      return {
        success: false,
        error: {
          code:        result.errorCode ?? 'E_PURCHASE_FAILED',
          message:     result.errorMessage ?? 'Purchase failed',
          recoverable: false,
        },
      };
    } catch (err: unknown) {
      const { code, message } = sanitizeIapError(err);
      logIap('iap:purchase:error', { code, message });
      const e = err as { code?: string; message?: string };
      return {
        success: false,
        error: {
          code:        e?.code ?? 'E_PURCHASE_FAILED',
          message:     e?.message ?? 'Purchase failed',
          recoverable: false,
        },
      };
    }
  }

  async getActiveSubscriptions(): Promise<Subscription[]> {
    this.assertReady();

    const { purchases } = await AppStoreBilling.getAvailablePurchases();
    const now = Date.now();

    return purchases
      .filter((p) => {
        if (!p.expiresDate) return true;
        return new Date(p.expiresDate).getTime() > now;
      })
      .map((p) => ({
        id:            p.transactionId,
        productId:     p.productId,
        platform:      'apple' as BillingPlatform,
        status:        'active' as const,
        startDate:     new Date(p.purchaseDate),
        expiryDate:    p.expiresDate ? new Date(p.expiresDate) : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        autoRenewing:  true,
      }));
  }

  async restorePurchases(): Promise<RestoredPurchase[]> {
    logIap('iap:restore:start');

    if (!this.ready) {
      await this.initialize();
    }

    try {
      const { purchases } = await AppStoreBilling.getAvailablePurchases();
      logIap('iap:restore:success', { count: purchases.length });

      return purchases.map((p) => ({
        productId:       p.productId,
        purchaseToken:   p.jwsRepresentation,
        transactionId:   p.transactionId,
        transactionDate: new Date(p.purchaseDate),
        platform:        'apple' as BillingPlatform,
      }));
    } catch (err) {
      const { code, message } = sanitizeIapError(err);
      logIap('iap:restore:error', { code, message });
      throw err;
    }
  }

  async finishTransaction(transactionId: string): Promise<void> {
    await AppStoreBilling.finishTransaction({ transactionId });
  }

  addEventListener(listener: BillingEventListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  dispose(): void {
    this.nativeListenerHandle?.remove();
    this.nativeListenerHandle = null;
    this.listeners.clear();
    this.ready = false;
  }

  // ─────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────

  private async fetchSubscriptionsWithRetry(): Promise<BillingProduct[]> {
    const sku = APPLE_VERIFIED_PRODUCT_ID;
    logIap('iap:subscriptions:fetch:start', { sku, platform: 'ios' });

    let lastError: unknown = null;

    for (let attempt = 1; attempt <= MAX_PRODUCT_FETCH_ATTEMPTS; attempt++) {
      const delay = RETRY_DELAY_BEFORE_ATTEMPT[attempt];
      if (delay) {
        await delayMs(delay);
      }

      try {
        const { products } = await AppStoreBilling.getSubscriptions({
          skus: [sku],
        });

        if (!products?.length) {
          logIap('iap:subscriptions:fetch:empty', { attempt });
          if (attempt === MAX_PRODUCT_FETCH_ATTEMPTS) {
            throw new ProductFetchEmptyError();
          }
          continue;
        }

        const mapped = products.map((p) => ({
          id:             p.productId,
          platform:       'apple' as BillingPlatform,
          title:          p.title,
          description:    p.description,
          price:          p.price,
          localizedPrice: p.price,
          priceValue:     p.priceValue ?? 0,
          currency:       p.currency,
          recurring:      true,
          billingPeriod:  'monthly' as const,
        }));

        const hasLocalizedPrice = mapped.some((p) => !!(p.localizedPrice || p.price));
        logIap('iap:subscriptions:fetch:success', {
          sku,
          count: mapped.length,
          hasLocalizedPrice,
        });

        return mapped;
      } catch (err) {
        lastError = err;
        if (err instanceof ProductFetchEmptyError) {
          if (attempt === MAX_PRODUCT_FETCH_ATTEMPTS) {
            throw err;
          }
          continue;
        }

        const { code, message } = sanitizeIapError(err);
        logIap('iap:subscriptions:fetch:fail', { attempt, code, message });

        if (attempt === MAX_PRODUCT_FETCH_ATTEMPTS) {
          throw Object.assign(new Error(message), { code });
        }
      }
    }

    throw lastError instanceof Error
      ? lastError
      : new ProductFetchEmptyError();
  }

  private notifyListeners(event: BillingEvent): void {
    this.listeners.forEach((l) => {
      try { l(event); } catch { /* ignore */ }
    });
  }

  private assertReady(): void {
    if (!this.ready) {
      throw new Error('[AppleBillingProvider] Not initialized. Call initialize() first.');
    }
  }
}
